/* eyebrow-reveal — a blob "wipe" reveal for eyebrow labels (GSAP).

   When an .eyebrow scrolls into view a coloured blob sweeps left → right across
   it — first growing in, then shrinking away — and the text is only revealed
   during the second (shrink) phase:

     1. blob grows  : scaleX 0 → 1, transform-origin LEFT   (grows L→R)
     2. text shown   : set under the still-covering blob
     3. blob shrinks : scaleX 1 → 0, transform-origin RIGHT (unmasks L→R)

   DOM is built at runtime:
     <p class="eyebrow is-wipe">
       <span class="eyebrow__inner">            (inline-block sizing context)
         <span class="eyebrow__text">…</span>   (the label, opacity-animated)
         <span class="eyebrow__wipe"></span>    (the blob)
       </span>
     </p>

   Progressive enhancement: if GSAP/IntersectionObserver are missing, the
   visitor prefers reduced motion, or we're in the Shopify theme editor, the
   eyebrows are left exactly as authored (no wrapping, no animation). */
(function () {
  const targets = document.querySelectorAll('.eyebrow, [data-eyebrow]');
  if (!targets.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = window.Shopify && window.Shopify.designMode;
  const ready = window.gsap && 'IntersectionObserver' in window;
  if (reduceMotion || inEditor || !ready) return;

  const gsap = window.gsap;

  targets.forEach((el) => {
    // Move the existing label content into a wrapper we can fade independently.
    const text = document.createElement('span');
    text.className = 'eyebrow__text';
    while (el.firstChild) text.appendChild(el.firstChild);

    const blob = document.createElement('span');
    blob.className = 'eyebrow__wipe';
    blob.setAttribute('aria-hidden', 'true');

    const inner = document.createElement('span');
    inner.className = 'eyebrow__inner';
    inner.appendChild(text);
    inner.appendChild(blob);
    el.appendChild(inner);
    el.classList.add('is-wipe');

    gsap.set(text, { autoAlpha: 0 });
    gsap.set(blob, { scaleX: 0, transformOrigin: 'left center', willChange: 'transform' });

    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      gsap.timeline()
        // 1. grow the blob L→R (left edge fixed, right edge advances right)
        .to(blob, { scaleX: 1, duration: 0.35, ease: 'power2.in' })
        // 2. text appears, still hidden under the full blob
        .set(text, { autoAlpha: 1 })
        // 3. re-anchor right so the blob now shrinks L→R, unmasking the text
        .set(blob, { transformOrigin: 'right center' })
        .to(blob, { scaleX: 0, duration: 0.5, ease: 'power2.out', onComplete: () => { blob.style.willChange = 'auto'; } }, '+=0.05');
      // Match text-reveal.js's trigger geometry exactly (threshold + rootMargin)
      // so the eyebrow starts at the same scroll point as its heading.
    }, { threshold: 0.2, rootMargin: '0px 0px -15% 0px' });

    // Observe the eyebrow's associated heading so the blob fires in sync with
    // the heading reveal (the eyebrow sits above the heading, so on its own it
    // would trigger earlier). Fall back to the eyebrow itself if none is found.
    io.observe(findHeading(el) || el);
  });

  // The [data-reveal] heading in the same section, found by walking up from the
  // eyebrow (staying inside the section) to the nearest shared ancestor that
  // holds one. Bounded to the section so we never sync to another section's
  // heading; returns null if this section has no reveal heading.
  function findHeading(el) {
    const section = el.closest('.shopify-section, section');
    if (!section) return null;
    let scope = el.parentElement;
    while (scope && section.contains(scope)) {
      const heading = scope.querySelector('[data-reveal]');
      if (heading) return heading;
      scope = scope.parentElement;
    }
    return null;
  }
})();
